/**
 * Smith Protective — Territory Kanban (Iter162).
 *
 * Drag-and-drop board grouped by pipeline_status. Uses @hello-pangea/dnd
 * (already installed for the main /kanban page).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import confetti from "canvas-confetti";
import {
  Shield, Loader2, RefreshCw, Target, ExternalLink, Trophy, Mail, Trash2,
  MoveRight, X, Star, PhoneCall, MapPin,
} from "lucide-react";

import Sidebar from "../../components/Sidebar";
import AppHeader from "../../components/SmithAppHeader";
import { ProspectsViewToggle } from "./SmithProspects";
import LogCallModal from "../../components/smith/LogCallModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NAVY = "#0B1120";
const usd = (n) => (n ? `$${Math.round(n).toLocaleString("en-US")}` : "—");
const usdShort = (n) => {
  if (!n) return "—";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1000)}k`;
  return `$${n}`;
};

const TIER_COLOR = {
  hot: "#DC2626", high: "#16A34A", qualified: "#EAB308",
  nurture: "#3B82F6", low: "#94A3B8",
};

export default function SmithKanban() {
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);   // Iter256 · std header
  const [total, setTotal] = useState(0);
  const [segment, setSegment] = useState("");
  const [segments, setSegments] = useState([]);
  // Iter211 — Signal timeline drawer state
  const [timelineProspect, setTimelineProspect] = useState(null);
  // Iter214 — Quick Log-Call modal
  const [logCallProspect, setLogCallProspect] = useState(null);
  const [onIcpOnly, setOnIcpOnly] = useState(false);

  // Iter200 — multi-select for bulk move/delete
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const toggleSelect = (id, ev) => {
    ev?.stopPropagation();
    setSelectedIds((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const bulkMove = async (target) => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    if (!confirm(`Mover ${ids.length} prospect${ids.length > 1 ? "s" : ""} para "${target}"?`)) return;
    setBulkBusy(true);
    try {
      const { data } = await axios.post(`${API}/smith/prospects/kanban-bulk-move`,
        { prospect_ids: ids, pipeline_status: target });
      toast.success(`Movidos ${data.moved} · ignorados ${data.skipped}`);
      clearSelection();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no bulk move");
    } finally { setBulkBusy(false); }
  };

  const bulkDelete = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    if (!confirm(`Excluir ${ids.length} prospect${ids.length > 1 ? "s" : ""}? Só funciona pra items em New Prospect/Researching/Junk.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await axios.post(`${API}/smith/prospects/kanban-bulk-delete`,
        { prospect_ids: ids });
      if (data.blocked > 0) {
        toast.warning(`Excluídos ${data.deleted} · bloqueados ${data.blocked} (com histórico)`);
      } else {
        toast.success(`Excluídos ${data.deleted}`);
      }
      clearSelection();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no bulk delete");
    } finally { setBulkBusy(false); }
  };

  const deleteOne = async (p) => {
    if (!confirm(`Excluir "${(p.company || {}).name || p.name}"?`)) return;
    try {
      await axios.delete(`${API}/smith/prospects/${p.id}`);
      toast.success("Excluído");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao excluir");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (segment) qs.set("segment", segment);
      if (onIcpOnly) qs.set("on_icp_only", "true");
      const { data } = await axios.get(`${API}/smith/kanban-v2?${qs.toString()}`);
      setColumns(data.columns || []);
      setTotal(data.total || 0);
      setLastUpdated(new Date());                // Iter256 · std header
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar kanban");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [segment, onIcpOnly]);
  useEffect(() => {
    axios.get(`${API}/smith/segments`)
      .then(({ data }) => setSegments(data.segments || []))
      .catch(() => {});
  }, []);

  const onDragEnd = async (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId
        && source.index === destination.index) return;

    // Iter227 · Functional setState prevents stale-closure races when a
    // background reload (`load()`) fires mid-drag or shortly after.
    let movedRef = null;
    let dstLabel = destination.droppableId;
    setColumns((prev) => {
      const next = prev.map((c) => ({ ...c, items: [...c.items] }));
      const srcCol = next.find((c) => c.id === source.droppableId);
      const dstCol = next.find((c) => c.id === destination.droppableId);
      if (!srcCol || !dstCol) return prev;
      const [moved] = srcCol.items.splice(source.index, 1);
      if (!moved) return prev;
      movedRef = moved;
      dstLabel = dstCol.label || dstCol.id;
      dstCol.items.splice(destination.index, 0, { ...moved, pipeline_status: dstCol.id });
      srcCol.count = srcCol.items.length;
      dstCol.count = dstCol.items.length;
      srcCol.acv_sum = srcCol.items.reduce((s, i) => s + Number(i.opportunity?.acv_est || 0), 0);
      dstCol.acv_sum = dstCol.items.reduce((s, i) => s + Number(i.opportunity?.acv_est || 0), 0);
      return next;
    });
    if (!movedRef) return;   // nothing was actually moved

    try {
      const { data } = await axios.post(`${API}/smith/prospects/${draggableId}/kanban-move`,
        { pipeline_status: destination.droppableId });
      if (destination.droppableId === "Won" && data.old_status !== "Won") {
        // 🎉 Closed-won celebration
        const duration = 2500;
        const end = Date.now() + duration;
        (function frame() {
          confetti({ particleCount: 4, angle: 60, spread: 55, origin: { x: 0 },
                     colors: ["#16A34A", "#DC2626", "#EAB308", "#3B82F6"] });
          confetti({ particleCount: 4, angle: 120, spread: 55, origin: { x: 1 },
                     colors: ["#16A34A", "#DC2626", "#EAB308", "#3B82F6"] });
          if (Date.now() < end) requestAnimationFrame(frame);
        }());
        toast.success(`🏆 CLOSED WON · ${movedRef.company?.name}`, { duration: 5000 });
      } else {
        toast.success(`Movido para "${dstLabel}"`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao mover — revertendo");
      load(); // revert on failure
    }
  };

  return (
    <div className="min-h-screen" style={{ background: NAVY, color: "#E2E8F0" }}>
      <Sidebar />
      <div className="lg:ml-[var(--al-sidebar-w,260px)]">
        <AppHeader
          testid="smith-kanban-header"
          title="Smith · Kanban"
          subtitle={`${total} accounts across ${columns.length} pipeline stages · drag & drop to reprioritize`}
          claireQuestion="Como uso o Smith · Kanban — mover leads entre stages, filtros por segmento e ICP fit?"
          lastUpdated={lastUpdated}
          onRefresh={load}
          refreshing={loading}
          logo={
            <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                 style={{ background: "linear-gradient(135deg,#1E3A8A 0%,#0F172A 100%)", color: "#DC2626" }}>
              <Shield className="w-5 h-5" />
            </div>
          }
        >
          <Link to="/smith" data-testid="smith-dashboard-nav"
            className="h-9 px-3 rounded-lg border border-white/15 text-[12.5px] font-bold text-white hover:border-[#DC2626]">
            Dashboard
          </Link>
          <Link to="/smith/prospects" data-testid="smith-prospects-nav"
            className="h-9 px-3 rounded-lg border border-white/15 text-[12.5px] font-bold text-white hover:border-[#DC2626]">
            Table
          </Link>
          <select value={segment} onChange={(e) => setSegment(e.target.value)}
            data-testid="kanban-segment-filter"
            className="h-9 px-2 rounded-lg bg-black/30 border border-white/15 text-[12px] font-bold text-white focus:border-[#DC2626] outline-none">
            <option value="">All segments</option>
            {segments.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <label data-testid="kanban-icp-toggle"
            className={"h-9 px-2 rounded-lg border text-[11.5px] font-bold inline-flex items-center gap-1.5 cursor-pointer "
                       + (onIcpOnly ? "border-emerald-500 bg-emerald-500/15 text-emerald-300" : "border-white/15 text-slate-300")}>
            <input type="checkbox" checked={onIcpOnly} onChange={(e) => setOnIcpOnly(e.target.checked)} className="sr-only" />
            {onIcpOnly ? "🎯 ICP only" : "All ICPs"}
          </label>
          <button type="button" onClick={load} disabled={loading}
            data-testid="kanban-refresh"
            className="h-9 px-3 rounded-lg border border-white/15 text-[12.5px] font-bold hover:border-[#DC2626] inline-flex items-center gap-1.5">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </AppHeader>

        {/* Iter249 · View toggle — Prospects (table) ↔ Kanban */}
        <div className="max-w-[1400px] mx-auto px-3 sm:px-6 pt-3">
          <ProspectsViewToggle current="kanban" />
        </div>

        <main className="px-3 sm:px-6 py-4 sm:py-6 pb-24 lg:pb-6" data-testid="smith-kanban-page">
          {loading && !columns.length ? (
            <div className="h-40 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-[#DC2626]" />
            </div>
          ) : (
            <DragDropContext onDragEnd={onDragEnd}>
              <div className="flex gap-3 overflow-x-auto pb-3" data-testid="kanban-board">
                {columns.map((col) => (
                  <Column key={col.id} col={col}
                          selectedIds={selectedIds}
                          onToggleSelect={toggleSelect}
                          onDeleteOne={deleteOne}
                          onOpenTimeline={setTimelineProspect}
                          onLogCall={(p) => setLogCallProspect(
                            { id: p.id, name: p.company?.name })} />
                ))}
              </div>
            </DragDropContext>
          )}
        </main>
      </div>

      {/* Iter200 — Floating toolbar for multi-select bulk operations */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 rounded-xl border border-white/15 shadow-2xl px-3 py-2 flex items-center gap-2"
             style={{ background: "rgba(11,17,32,0.98)" }}
             data-testid="kanban-bulk-toolbar">
          <span className="text-[12px] font-extrabold text-white mr-2">
            {selectedIds.size} selecionado{selectedIds.size > 1 ? "s" : ""}
          </span>
          <div className="flex items-center gap-1">
            {["Researching", "Qualified", "Contacted", "Proposal Sent", "Won", "Lost", "Junk"].map((target) => (
              <button key={target} type="button" onClick={() => bulkMove(target)}
                      disabled={bulkBusy}
                      data-testid={`kanban-bulk-move-${target.toLowerCase().replace(/\s+/g, "-")}`}
                      className="h-7 px-2 rounded text-[10.5px] font-bold text-slate-200 hover:bg-white/10 border border-white/10 inline-flex items-center gap-1 disabled:opacity-50">
                <MoveRight className="w-2.5 h-2.5" /> {target}
              </button>
            ))}
          </div>
          <div className="w-px h-6 bg-white/15 mx-1" />
          <button type="button" onClick={bulkDelete} disabled={bulkBusy}
                  data-testid="kanban-bulk-delete-btn"
                  title="Só permitido em cards ainda em New Prospect/Researching/Junk"
                  className="h-7 px-2 rounded text-[10.5px] font-bold text-white bg-red-500/70 hover:bg-red-600 inline-flex items-center gap-1 disabled:opacity-50">
            <Trash2 className="w-2.5 h-2.5" /> Excluir
          </button>
          <button type="button" onClick={clearSelection}
                  data-testid="kanban-bulk-clear-btn"
                  className="h-7 w-7 rounded hover:bg-white/10 text-slate-400 flex items-center justify-center">
            <X className="w-3 h-3" />
          </button>
        </div>
      )}
      {timelineProspect && (
        <SignalTimelineDrawer
          prospect={timelineProspect}
          onClose={() => setTimelineProspect(null)} />
      )}
      {logCallProspect && (
        <LogCallModal
          prospectId={logCallProspect.id}
          companyName={logCallProspect.name}
          onClose={() => setLogCallProspect(null)}
          onLogged={() => { setLogCallProspect(null); load(); }} />
      )}
    </div>
  );
}

// Iter227 · Origin badge — shows lead source with descriptive tooltip
function OriginBadge({ p }) {
  const src = p?.source || "";
  const importSrc = p?.import_meta?.source;
  const aiRun = p?.ai_search_run_id;
  const importBatch = p?.import_meta?.batch_id;
  const importFile = p?.import_meta?.filename;

  // Determine primary origin + label + tooltip + color
  let label = "Origem", tooltip = "", color = "#64748B", icon = "•";
  if (aiRun || src === "ai_search_os" || src === "ai_search") {
    label = "Smith AI Search";
    tooltip = `Origem: Smith AI Search${aiRun ? ` · run ${String(aiRun).slice(0, 8)}` : ""}`
      + (p?.segment ? ` · ${String(p.segment).replace(/_/g, " ")}` : "")
      + (p?.property?.city ? ` · ${p.property.city}` : "");
    color = "#8B5CF6"; icon = "🤖";
  } else if (src === "ai_market_signal" || p?.source_signal_id) {
    label = "Buying Signal";
    tooltip = `Origem: Buying Signal (Market Scanner)${p?.source_signal_id ? ` · sig ${String(p.source_signal_id).slice(0, 8)}` : ""}`;
    color = "#F59E0B"; icon = "⚡";
  } else if (importSrc === "google_places" || importSrc === "google_maps") {
    label = "Google Maps";
    tooltip = `Origem: Google Maps API${importBatch ? ` · batch ${importBatch.slice(0, 8)}` : ""}`;
    color = "#0EA5E9"; icon = "📍";
  } else if (importSrc === "manual_import" || src === "manual_import") {
    label = "Import CSV";
    tooltip = `Origem: Import manual${importFile ? ` · ${importFile}` : ""}`
      + (importBatch ? ` · batch ${importBatch.slice(0, 8)}` : "");
    color = "#22C55E"; icon = "📥";
  } else if (importSrc === "osm_overpass" || src === "osm_overpass") {
    label = "OpenStreetMap";
    tooltip = "Origem: OpenStreetMap Overpass";
    color = "#10B981"; icon = "🗺️";
  } else if (importSrc) {
    label = importSrc.slice(0, 14);
    tooltip = `Origem: ${importSrc}`
      + (importBatch ? ` · batch ${importBatch.slice(0, 8)}` : "");
    color = "#64748B"; icon = "•";
  } else if (src) {
    label = src.slice(0, 14);
    tooltip = `Origem: ${src}`;
    color = "#64748B"; icon = "•";
  } else {
    // No known origin data — surface as "Manual" default
    label = "Manual";
    tooltip = "Origem: entrada manual / desconhecida — sem metadados";
    color = "#475569"; icon = "•";
  }

  return (
    <span data-testid={`kanban-origin-${p.id}`}
      title={tooltip}
      className="text-[8.5px] font-extrabold uppercase tracking-widest px-1.5 py-0.5 rounded shrink-0 inline-flex items-center gap-0.5 border cursor-help"
      style={{ borderColor: `${color}66`, background: `${color}22`, color }}>
      <span className="text-[10px]" aria-hidden>{icon}</span>
      <span className="hidden sm:inline">{label}</span>
    </span>
  );
}


function Column({ col, selectedIds, onToggleSelect, onDeleteOne, onOpenTimeline, onLogCall }) {
  // Iter228 · Google Maps route for the current column (up to 10 stops)
  const openMapsRoute = () => {
    const withAddress = col.items.filter((p) =>
      p.property?.city || p.property?.address || p.company?.name);
    if (withAddress.length === 0) return;
    const stops = withAddress.slice(0, 10).map((p) => {
      const city = p.property?.city || "";
      const state = p.property?.state || "";
      const addr = p.property?.address || "";
      const name = p.company?.name || "";
      const parts = [addr, name, city, state, "US"].filter(Boolean).join(", ");
      return encodeURIComponent(parts);
    }).filter(Boolean);
    if (!stops.length) return;
    const url = `https://www.google.com/maps/dir/${stops.join("/")}/data=!4m2!4m1!3e0`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="shrink-0 w-[280px] rounded-2xl border p-2"
         data-testid={`kanban-col-${col.id}`}
         style={{ background: "rgba(15,23,42,0.7)", borderColor: `${col.color}33` }}>
      <div className="flex items-center justify-between px-2 py-2 border-b mb-2"
           style={{ borderColor: `${col.color}22` }}>
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: col.color }} />
          <div className="font-extrabold text-white text-[13px] truncate">{col.label}</div>
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-white/10 text-slate-300 shrink-0">
            {col.count}
          </span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <div className="text-[10.5px] font-bold text-emerald-400 tabular-nums">
            {usdShort(col.acv_sum)}
          </div>
          {/* Iter228 · Google Maps route per column */}
          {col.items.length > 0 && (
            <button type="button" onClick={openMapsRoute}
              data-testid={`kanban-col-maps-${col.id.toLowerCase().replace(/\s+/g, "-")}`}
              title={`Abrir rota Google Maps · ${Math.min(col.items.length, 10)} paradas`}
              className="w-5 h-5 rounded flex items-center justify-center text-emerald-300 hover:text-white hover:bg-emerald-500/30 transition-colors">
              <MapPin className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
      <Droppable droppableId={col.id}>
        {(provided, snapshot) => (
          <div ref={provided.innerRef} {...provided.droppableProps}
               className="space-y-2 min-h-[80px] max-h-[calc(100vh-260px)] overflow-y-auto p-0.5 rounded-lg transition-colors"
               style={{ background: snapshot.isDraggingOver ? `${col.color}12` : "transparent" }}>
            {col.items.length === 0 && (
              <div className="text-[11px] text-slate-500 italic px-2 py-3 text-center">
                Nenhum prospect
              </div>
            )}
            {col.items.map((p, idx) => {
              const isSelected = selectedIds.has(p.id);
              return (
              <Draggable key={p.id} draggableId={p.id} index={idx}>
                {(prov, snap) => (
                  <div ref={prov.innerRef} {...prov.draggableProps} {...prov.dragHandleProps}
                       data-testid={`kanban-card-${p.id}`}
                       onDoubleClick={() => onOpenTimeline?.(p)}
                       title="Duplo-clique para ver timeline de Buying Signals"
                       className={"rounded-lg border p-2.5 cursor-grab active:cursor-grabbing "
                                  + (snap.isDragging ? "shadow-2xl " : "")
                                  + (isSelected ? "ring-2 ring-[#DC2626]/60 " : "")}
                       style={{ background: "rgba(11,17,32,0.85)",
                                borderColor: snap.isDragging ? col.color : (isSelected ? "#DC2626" : "rgba(255,255,255,0.08)"),
                                ...prov.draggableProps.style }}>
                    <div className="flex items-start justify-between gap-1.5 mb-1">
                      <div className="flex items-start gap-1.5 min-w-0 flex-1">
                        <input type="checkbox" checked={isSelected}
                               onChange={(e) => onToggleSelect(p.id, e)}
                               onClick={(e) => e.stopPropagation()}
                               onMouseDown={(e) => e.stopPropagation()}
                               data-testid={`kanban-select-${p.id}`}
                               className="mt-0.5 accent-[#DC2626] cursor-pointer" />
                        <div className="min-w-0 flex-1">
                          <Link to={`/smith/prospects/${p.id}`}
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                                className="font-bold text-white text-[12.5px] hover:text-[#DC2626] truncate block">
                            {p.company?.name}
                          </Link>
                          <div className="text-[10.5px] text-slate-400 capitalize truncate flex items-center gap-1.5">
                            <span>{(p.segment || "").replace("_", " ")} · {p.property?.city}</span>
                            {/* Iter227 · Origin badge */}
                            <OriginBadge p={p} />
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-0.5 shrink-0 tabular-nums text-[11px] font-extrabold"
                           style={{ color: TIER_COLOR[p.tier?.tier] || "#94A3B8" }}>
                        <span>{p.tier?.icon}</span>
                        <span>{p.opportunity_score}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-2 text-[10.5px]">
                      <div className="flex items-center gap-1.5">
                        {p.icp_match?.on_icp && (
                          <span className="px-1 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-bold text-[9px] uppercase">
                            ICP
                          </span>
                        )}
                        {p.champion_dm?.email && (
                          <span className="px-1 py-0.5 rounded bg-amber-400/25 text-amber-200 font-bold text-[9px] uppercase inline-flex items-center gap-0.5 border border-amber-400/40"
                                data-testid={`kanban-champion-${p.id}`}
                                title={`Champion: ${p.champion_dm.name || p.champion_dm.email} · ${p.champion_dm.reply_count || 2}+ replies`}>
                            <Star className="w-2.5 h-2.5 fill-current" /> CHAMP
                          </span>
                        )}
                        {p.security?.current_provider && (
                          <span className="text-slate-500 truncate max-w-[100px]">
                            {p.security.current_provider}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Link to={`/smith/outreach?prospect_id=${p.id}&tab=compose`}
                              data-testid={`kanban-card-outreach-${p.id}`}
                              onClick={(e) => e.stopPropagation()}
                              title="Enviar email via Outreach AI (pré-carrega dados)"
                              className="w-6 h-6 rounded flex items-center justify-center bg-emerald-500/15 hover:bg-emerald-500/30 text-emerald-300">
                          <Mail className="w-3 h-3" />
                        </Link>
                        <button type="button"
                              onClick={(e) => { e.stopPropagation(); onLogCall?.(p); }}
                              data-testid={`kanban-card-logcall-${p.id}`}
                              title="Log call (3 segundos)"
                              className="w-6 h-6 rounded flex items-center justify-center bg-sky-500/15 hover:bg-sky-500/30 text-sky-300">
                          <PhoneCall className="w-3 h-3" />
                        </button>
                        {(col.id === "New Prospect" || col.id === "Researching" || col.id === "Junk") && (
                          <button type="button"
                                  onClick={(e) => { e.stopPropagation(); onDeleteOne(p); }}
                                  data-testid={`kanban-card-delete-${p.id}`}
                                  title="Excluir prospect"
                                  className="w-6 h-6 rounded flex items-center justify-center bg-red-500/10 hover:bg-red-500/30 text-red-300">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                        <span className="text-emerald-400 font-bold tabular-nums">
                          {usdShort(p.opportunity?.acv_est)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </Draggable>
              );
            })}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  );
}


// Iter211 — Signal Timeline Drawer (right-side panel on Kanban card open)
function SignalTimelineDrawer({ prospect, onClose }) {
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/smith/prospects/${prospect.id}/signals-timeline`);
        setSignals(data.signals || []);
      } catch (_) { /* silent */ }
      finally { setLoading(false); }
    })();
  }, [prospect.id]);

  const CAT_COLOR = {
    expansion:       "#10B981",
    hiring:          "#3B82F6",
    public_contract: "#F59E0B",
    event:           "#A855F7",
    growth:          "#F97316",
  };
  const CAT_LABEL = {
    expansion: "Expansão", hiring: "Contratação",
    public_contract: "Licitação", event: "Evento", growth: "Crescimento",
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end"
         data-testid="kanban-signal-timeline-drawer">
      <div className="flex-1 bg-black/60" onClick={onClose} />
      <div className="w-full max-w-md h-full overflow-y-auto p-5 border-l border-white/10"
           style={{ background: "#0B1120" }}
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div className="min-w-0">
            <h3 className="text-[16px] font-black text-white truncate">
              {(prospect.company || {}).name || "Prospect"}
            </h3>
            <p className="text-[11px] text-slate-400 mt-0.5">
              {loading ? "Carregando timeline…" : `${signals.length} sinal(is) detectado(s)`}
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-white/5 text-slate-400 flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </div>
        {loading && (
          <div className="text-center py-10 text-slate-500 text-[12px]"
               data-testid="kanban-timeline-loading">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Buscando…
          </div>
        )}
        {!loading && signals.length === 0 && (
          <div className="text-center py-10 text-slate-500 text-[12px] rounded-lg border border-dashed border-white/10"
               data-testid="kanban-timeline-empty">
            Nenhum sinal detectado ainda para <b>{(prospect.company || {}).name}</b>.
            Rode <b className="text-orange-300">Find Buying Signals</b> em /smith/ai-search/signals.
          </div>
        )}
        <ol className="relative border-l border-white/10 ml-3 space-y-3">
          {signals.map((s) => {
            const color = CAT_COLOR[s.category] || "#94A3B8";
            const label = CAT_LABEL[s.category] || s.category;
            return (
              <li key={s.id} data-testid={`kanban-timeline-item-${s.id}`}
                  className="pl-4 relative">
                <span className="absolute -left-[7px] top-1.5 w-3 h-3 rounded-full border-2"
                      style={{ background: color, borderColor: "#0B1120" }} />
                <div className="flex items-center gap-2 flex-wrap text-[10px] mb-1">
                  <span className="font-black uppercase tracking-widest" style={{ color }}>{label}</span>
                  {s.score && <span className="text-slate-400">· score {s.score}</span>}
                  {s.signal_date && <span className="text-slate-500">· {s.signal_date}</span>}
                  <span className="text-slate-600">· {s.source === "market" ? "market" : "dossier"}</span>
                </div>
                <div className="text-[12.5px] font-bold text-white leading-tight">{s.title}</div>
                {s.summary && (
                  <p className="text-[11px] text-slate-400 mt-1 line-clamp-3">{s.summary}</p>
                )}
                {s.source_url && (
                  <a href={s.source_url} target="_blank" rel="noreferrer"
                     className="text-[10.5px] text-sky-400 hover:text-sky-300 mt-1.5 inline-flex items-center gap-1">
                    <ExternalLink className="w-2.5 h-2.5" /> Ver fonte
                  </a>
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}

